import { useEffect, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { FilterOptions } from '../services/api'
import { filtersApi } from '../services/api'

interface InitialValues {
  name: string
  filters: Record<string, unknown>
}

interface Props {
  onCreate: (name: string, filters: Record<string, unknown>) => Promise<boolean>
  initialValues?: InitialValues
  externalError?: string
  filterOptions?: FilterOptions
  session: Session
  onCancel: () => void
}

const STEPS = ['Categoria', 'Dove', 'Parole chiave', 'Nome']

const fieldCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-sky-500'

function buildSuggestedName(
  region: string,
  enti: string[],
  keywords: string[]
): string {
  let locPart = ''
  if (region) {
    locPart = `in ${region}`
  } else if (enti.length > 0) {
    locPart = enti.slice(0, 2).join(', ')
  }

  if (locPart && keywords.length > 0) return `${keywords[0]} ${locPart}`
  if (locPart) return locPart
  if (keywords.length > 0) return keywords[0]
  return ''
}

export default function AlertForm({ onCreate, initialValues, externalError, filterOptions, session, onCancel }: Props) {
  const f = initialValues?.filters ?? {}

  const [step, setStep] = useState(0)

  const [name, setName] = useState(initialValues?.name ?? '')
  const [nameTouched, setNameTouched] = useState(!!(initialValues?.name))
  const [region, setRegion] = useState((f.region as string) ?? '')

  const initCategories: string[] = Array.isArray(f.categories) ? (f.categories as string[]) :
    f.category ? [f.category as string] : []
  const [allSelected, setAllSelected] = useState(initCategories.length === 0)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initCategories)

  const [keywords, setKeywords] = useState<string[]>(
    Array.isArray(f.keywords) ? (f.keywords as string[]) :
    f.keyword ? [f.keyword as string] : []
  )
  const [kwInput, setKwInput] = useState('')
  const [kwError, setKwError] = useState('')
  const [filterError, setFilterError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const kwInputRef = useRef<HTMLInputElement>(null)

  const initEnti: string[] = Array.isArray(f.enti) ? (f.enti as string[]) : []
  const [selectedEnti, setSelectedEnti] = useState<string[]>(initEnti)
  const [locationMode, setLocationMode] = useState<'region' | 'ente'>(
    initEnti.length > 0 ? 'ente' : 'region'
  )
  const [entiSearch, setEntiSearch] = useState('')
  const [entiResults, setEntiResults] = useState<string[]>([])
  const [entiLoading, setEntiLoading] = useState(false)

  const regions = filterOptions?.regions ?? []
  const categoryOptions = filterOptions?.categories ?? []

  // Auto-suggest name when entering last step (only if user hasn't typed one)
  useEffect(() => {
    if (step === 3 && !nameTouched) {
      const suggested = buildSuggestedName(region, selectedEnti, keywords)
      if (suggested) setName(suggested.charAt(0).toUpperCase() + suggested.slice(1))
    }
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // Enti typeahead search
  useEffect(() => {
    if (entiSearch.trim().length < 2) { setEntiResults([]); return }
    const timer = setTimeout(async () => {
      setEntiLoading(true)
      try {
        const results = await filtersApi.searchEnti(entiSearch, session)
        setEntiResults(results.filter(e => !selectedEnti.includes(e)))
      } catch { setEntiResults([]) }
      finally { setEntiLoading(false) }
    }, 300)
    return () => clearTimeout(timer)
  }, [entiSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleCategoryClick(cat: string) {
    if (allSelected) {
      // Deselect clicked category, keep all others selected
      setAllSelected(false)
      setSelectedCategories(categoryOptions.filter(c => c !== cat))
    } else {
      const next = selectedCategories.includes(cat)
        ? selectedCategories.filter(c => c !== cat)
        : [...selectedCategories, cat]
      if (next.length === 0 || next.length === categoryOptions.length) {
        setAllSelected(true)
        setSelectedCategories([])
      } else {
        setSelectedCategories(next)
      }
    }
  }

  function addKeyword(raw: string) {
    const kw = raw.trim()
    if (!kw || keywords.includes(kw) || keywords.length >= 3) return
    if (kw.length < 3) { setKwError('La parola chiave deve avere almeno 3 lettere.'); return }
    if (/[*?+[\](){}^$|\\]/.test(kw)) { setKwError('I caratteri speciali come * non sono ammessi.'); return }
    setKwError('')
    setKeywords([...keywords, kw])
    setKwInput('')
    kwInputRef.current?.blur()
  }

  async function handleSubmit() {
    if (submitting) return
    const filters: Record<string, unknown> = {}
    if (region) filters.region = region
    if (!allSelected && selectedCategories.length > 0) filters.categories = selectedCategories
    if (keywords.length > 0) filters.keywords = keywords
    if (selectedEnti.length > 0) filters.enti = selectedEnti
    if (!filters.region && !filters.keywords && !filters.enti) {
      setFilterError('Seleziona almeno una regione, una parola chiave o un ente. Le categorie da sole non sono sufficienti.')
      setStep(1)
      return
    }
    setFilterError('')
    setSubmitting(true)
    try {
      const ok = await onCreate(name, filters)
      if (!ok) setSubmitting(false)
    } catch {
      setSubmitting(false)
    }
  }

  const isLastStep = step === STEPS.length - 1

  // ── Step 0: Categoria ────────────────────────────────────────────────────
  function renderStepCategoria() {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-500">Deseleziona le categorie che <strong>non</strong> ti interessano.</p>
        {categoryOptions.length === 0 ? (
          <p className="text-sm text-gray-400">Caricamento categorie…</p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => { setAllSelected(true); setSelectedCategories([]) }}
                className={`text-xs font-medium rounded-full px-4 py-1.5 border transition-colors ${
                  allSelected
                    ? 'bg-sky-500 border-sky-500 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-sky-300 hover:text-sky-500'
                }`}
              >
                Tutte
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {categoryOptions.map(cat => {
                const active = allSelected || selectedCategories.includes(cat)
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategoryClick(cat)}
                    className={`text-xs font-medium rounded-full px-3 border transition-colors h-9 flex items-center justify-center text-center ${
                      active
                        ? 'bg-teal-500 border-teal-500 text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-teal-400 hover:text-teal-600'
                    }`}
                  >
                    {cat}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Step 1: Dove (regione + ente) ────────────────────────────────────────
  function renderStepDove() {
    return (
      <div className="flex flex-col gap-5">
        {filterError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {filterError}
          </p>
        )}

        {/* Toggle */}
        <div className="flex rounded-lg border border-sky-200 overflow-hidden self-start w-full">
          <button
            type="button"
            onClick={() => {
              setLocationMode('region')
              setSelectedEnti([])
              setEntiSearch('')
              setEntiResults([])
            }}
            className={`flex-1 text-sm font-medium px-4 py-2 transition-colors ${
              locationMode === 'region'
                ? 'bg-sky-600 text-white'
                : 'bg-white text-sky-600 hover:bg-sky-50'
            }`}
          >
            Regione
          </button>
          <button
            type="button"
            onClick={() => {
              setLocationMode('ente')
              setRegion('')
            }}
            className={`flex-1 text-sm font-medium px-4 py-2 border-l border-sky-200 transition-colors ${
              locationMode === 'ente'
                ? 'bg-sky-600 text-white'
                : 'bg-white text-sky-600 hover:bg-sky-50'
            }`}
          >
            Ente specifico
          </button>
        </div>

        {/* Region panel */}
        {locationMode === 'region' && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-500">Seleziona una regione. Puoi saltare questo passo se non vuoi filtrare per area geografica.</p>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                {regions.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRegion(r)}
                    className={`text-xs font-medium rounded-full px-3 border transition-colors h-9 flex items-center justify-center text-center ${
                      region === r
                        ? 'bg-sky-500 border-sky-500 text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-sky-300 hover:text-sky-500'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Ente panel */}
        {locationMode === 'ente' && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-500">Cerca un ente pubblico specifico (max 3).</p>
            <div className="relative">
              <input
                className={fieldCls}
                placeholder={selectedEnti.length >= 3 ? 'Limite raggiunto' : 'Cerca ente…'}
                value={entiSearch}
                disabled={selectedEnti.length >= 3}
                onChange={e => setEntiSearch(e.target.value)}
              />
              {entiSearch.trim().length >= 2 && selectedEnti.length < 3 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto z-50">
                  {entiLoading ? (
                    <p className="text-xs text-gray-400 px-3 py-2">Ricerca in corso…</p>
                  ) : entiResults.length > 0 ? (
                    entiResults.map(e => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => { setSelectedEnti([...selectedEnti, e]); setEntiSearch(''); setEntiResults([]) }}
                        className="w-full text-left text-xs px-3 py-2 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                      >
                        {e}
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 px-3 py-2">Nessun ente trovato</p>
                  )}
                </div>
              )}
            </div>
            {selectedEnti.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-0.5">
                {selectedEnti.map(e => (
                  <span key={e} className="flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 bg-sky-500 border border-sky-500 text-white">
                    {e}
                    <button
                      type="button"
                      onClick={() => setSelectedEnti(selectedEnti.filter(x => x !== e))}
                      className="opacity-70 hover:opacity-100 leading-none ml-0.5"
                    >×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ── Step 2: Parole chiave ────────────────────────────────────────────────
  const KW_EXAMPLES = ['istruttore', 'funzionario', 'contabile', 'dirigente', 'medico', 'informatico', 'assistente sociale', 'infermiere', 'geometra', 'educatore', 'agente di polizia', 'farmacista', 'ingegnere', 'operaio', 'esperto']

  function renderStepKeywords() {
    const availableExamples = KW_EXAMPLES.filter(e => !keywords.includes(e))
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-500">
          Aggiungi fino a 3 parole chiave da cercare nel testo del bando. Questo campo è opzionale.
        </p>
        <input
          ref={kwInputRef}
          className={fieldCls}
          placeholder={keywords.length >= 3 ? 'Limite raggiunto' : 'Scrivi e premi Invio o virgola…'}
          value={kwInput}
          disabled={keywords.length >= 3}
          onChange={e => {
            const val = e.target.value
            if (val.endsWith(',')) {
              addKeyword(val.slice(0, -1))
            } else {
              setKwInput(val)
              if (kwError) setKwError('')
            }
          }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(kwInput) } }}
        />
        {kwError && <p className="text-xs text-red-600 -mt-1">{kwError}</p>}
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {keywords.map(kw => (
              <span key={kw} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full px-2.5 py-1">
                {kw}
                <button type="button" onClick={() => setKeywords(keywords.filter(k => k !== kw))} className="text-gray-400 hover:text-red-500 leading-none">×</button>
              </span>
            ))}
          </div>
        )}
        {keywords.length < 3 && availableExamples.length > 0 && (
          <div className="flex flex-col gap-1.5 pt-1">
            <p className="text-xs text-gray-400">Esempi — clicca per aggiungere:</p>
            <div className="flex flex-wrap gap-1.5">
              {availableExamples.map(ex => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => addKeyword(ex)}
                  className="text-xs rounded-full px-2.5 py-1 border border-dashed border-gray-300 text-gray-500 hover:border-sky-400 hover:text-sky-600 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Step 3: Nome ─────────────────────────────────────────────────────────
  function renderStepNome() {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-500">Dai un nome all'alert per riconoscerlo facilmente.</p>
        {externalError && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {externalError}
          </p>
        )}
        <input
          className={fieldCls}
          placeholder="Es. Informatica in Lombardia"
          value={name}
          onChange={e => { setName(e.target.value); setNameTouched(true) }}
          required
          maxLength={200}
          autoFocus
        />
        {!nameTouched && name && (
          <p className="text-xs text-gray-400">Suggerimento automatico — puoi modificarlo.</p>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 min-h-65">
      {/* Step title */}
      <div>
        <p className="text-sm font-semibold text-sky-600 uppercase tracking-wide mb-1">{STEPS[step]}</p>
      </div>

      {/* Step content */}
      <div className="flex-1">
        {step === 0 && renderStepCategoria()}
        {step === 1 && renderStepDove()}
        {step === 2 && renderStepKeywords()}
        {step === 3 && renderStepNome()}
      </div>

      {/* Navigation footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        {/* Back / Cancel */}
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep(s => s - 1)}
            className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            ← Indietro
          </button>
        ) : (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          >
            Annulla
          </button>
        )}

        {/* Step dots */}
        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all ${i === step ? 'w-4 h-2 bg-sky-600' : 'w-2 h-2 bg-gray-200'}`}
            />
          ))}
        </div>

        {/* Next / Save */}
        {isLastStep ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="text-sm font-medium bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {submitting ? 'Salvataggio…' : 'Salva'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setStep(s => s + 1)}
            className="text-sm font-medium bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Avanti →
          </button>
        )}
      </div>
    </div>
  )
}
